import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { GoogleAuthButton } from "./GoogleAuthButton";
import { ConsentCheckbox } from "@/components/legal/ConsentCheckbox";
import { trackEvent, identifyUser } from "@/lib/analytics";
import { Mail, KeyRound } from "lucide-react";
import { nextAwareRedirectUrl } from "@/lib/auth-redirect";

interface AuthFormProps {
  onSignUpSuccess: (email: string) => void;
}

export const AuthForm = ({ onSignUpSuccess }: AuthFormProps) => {
  const [mode, setMode] = useState<"magic" | "password" | "signup">("magic");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { toast.error("Email is required"); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: nextAwareRedirectUrl() },
      });
      if (error) throw error;
      trackEvent("magic_link_sent", { method: "email" });
      toast.success("Magic link sent! Check your email to sign in.");
    } catch (error: any) {
      toast.error(error.message || "Failed to send magic link");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email and password are required"); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data?.user) {
        trackEvent("login", { method: "password" });
        identifyUser(data.user.id, { email });
        toast.success("Successfully signed in!");
      }
    } catch (error: any) {
      if (error.message.includes("Invalid login credentials")) {
        toast.error("Invalid email or password. Please try again.");
      } else {
        toast.error(error.message || "Sign in failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email and password are required"); return; }
    if (!consentGiven) { toast.error("Please accept the terms and privacy policy"); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      trackEvent("signup", { method: "email" });
      if (data?.user) identifyUser(data.user.id, { email });
      toast.success("Account created! Check your email to verify.");
      onSignUpSuccess(email);
    } catch (error: any) {
      if (error.message.includes("already registered")) {
        toast.error("This email is already registered. Please sign in instead.");
      } else {
        toast.error(error.message || "Sign up failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold">
          {mode === "signup" ? "Create Account" : "Sign In"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {mode === "signup"
            ? "Create your account to get started"
            : "Sign in to your Veylo AI account"}
        </p>
      </div>

      {/* Primary: Google */}
      <GoogleAuthButton />

      {/* Divider */}
      <div className="relative my-6">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs uppercase tracking-wide text-muted-foreground">
          or continue with email
        </span>
      </div>

      {/* Sign-up view */}
      {mode === "signup" ? (
        <form onSubmit={handleSignUp} className="space-y-4">
          <Input
            type="email"
            placeholder="Email"
            aria-label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="Password"
            aria-label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <ConsentCheckbox
            checked={consentGiven}
            onCheckedChange={setConsentGiven}
            context="signup"
          />
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
          <div className="mt-2 flex flex-col items-center gap-2 text-sm">
            <button type="button" onClick={() => setMode("magic")} className="text-primary hover:underline">
              Already have an account? Sign In
            </button>
          </div>
        </form>
      ) : (
        <>
          {/* Secondary method toggle */}
          <div className="flex rounded-lg border bg-muted/40 p-1 mb-4">
            <button
              type="button"
              onClick={() => setMode("magic")}
              className={`flex-1 flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors ${
                mode === "magic"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Mail className="w-4 h-4" />
              Magic Link
            </button>
            <button
              type="button"
              onClick={() => setMode("password")}
              className={`flex-1 flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors ${
                mode === "password"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <KeyRound className="w-4 h-4" />
              Password
            </button>
          </div>

          {mode === "magic" && (
            <form onSubmit={handleMagicLink} className="space-y-4">
              <Input
                type="email"
                placeholder="Email"
                aria-label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Button type="submit" variant="outline" className="w-full h-11" disabled={loading}>
                <Mail className="w-4 h-4 mr-2" />
                {loading ? "Sending..." : "Send Magic Link"}
              </Button>
            </form>
          )}

          {mode === "password" && (
            <form onSubmit={handlePasswordSignIn} className="space-y-4">
              <Input
                type="email"
                placeholder="Email"
                aria-label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                type="password"
                placeholder="Password"
                aria-label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Button type="submit" variant="outline" className="w-full h-11" disabled={loading}>
                <KeyRound className="w-4 h-4 mr-2" />
                {loading ? "Signing In..." : "Sign In with Password"}
              </Button>
            </form>
          )}

          <div className="mt-4 flex flex-col items-center gap-2 text-sm">
            <button type="button" onClick={() => setMode("signup")} className="text-primary hover:underline">
              Need an account? Sign Up
            </button>
          </div>
        </>
      )}
    </Card>
  );
};
